"use client";

import { c as _c } from "react/compiler-runtime";
import { Suspense } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import type { DiscoveryNavLinkView } from "@repo/sanity-utils/discoveryChrome";
import { linkTargetProps } from "@repo/sanity-utils/discoveryChrome";
import { Menu } from "@repo/ui/mantine";
import { Typography } from "./typography";
import styles from "./DiscoveryHeaderNav.module.css";
import { useDiscoveryMobileMenuClose } from "./DiscoveryMobileMenuClose";
import { DiscoveryNavLinkIcon } from "./DiscoveryNavLinkIcon";
export type DiscoveryHeaderAuthUser = {
  displayName: string;
  initials: string;
  isParent: boolean;
  isStudent: boolean;
};
const EMPTY_LINKS: DiscoveryNavLinkView[] = [];
function authRedirectHref(path: string, redirect: string) {
  return `${path}?redirect=${encodeURIComponent(redirect)}`;
}

/** Absolute return URL for Accounts (`SaveRedirectParam` requires absolute). */
function absoluteRedirectUrl(redirectBase: string, redirectPath: string) {
  const base = redirectBase.replace(/\/+$/, "");
  return `${base}${redirectPath.startsWith("/") ? redirectPath : `/${redirectPath}`}`;
}
type AuthProps = {
  user: DiscoveryHeaderAuthUser | null;
  /** Sanity account-menu links, already campus-resolved. */
  accountLinks?: DiscoveryNavLinkView[];
  parentPortalHref: string;
  loginPath: string;
  logoutPath: string;
  /** Accounts `/register` URL (no query). */
  signUpHref: string;
  /**
   * Public discovery base (origin + optional basePath, no trailing slash).
   * Concatenated with usePathname() (+ query) for the Sign up redirect param.
   */
  redirectOrigin: string;
  /**
   * `bar` is the header actions row (guest CTAs hidden below the desktop nav
   * breakpoint). `menu` is the mobile drawer and only renders guest CTAs.
   */
  placement?: "bar" | "menu";
};
function Initials(t0) {
  const $ = _c(2);
  const {
    initials
  } = t0;
  let t1;
  if ($[0] !== initials) {
    t1 = <span className={styles.initials} aria-hidden={true}><Typography as="span" variant="caption">{initials}</Typography></span>;
    $[0] = initials;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function AccountChipFace(t0) {
  const $ = _c(7);
  const {
    user
  } = t0;
  let t1;
  if ($[0] !== user.initials) {
    t1 = <Initials initials={user.initials} />;
    $[0] = user.initials;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== user.displayName) {
    t2 = <Typography as="span" variant="buttonSm" className={styles.accountName}>{user.displayName}</Typography>;
    $[2] = user.displayName;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <>{t1}{t2}</>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function menuItemIcon(link: DiscoveryNavLinkView) {
  if (!link.iconUrl && !link.lucideIcon) return undefined;
  return <DiscoveryNavLinkIcon link={link} variant="inline" />;
}
function LogoutItem(t0) {
  const $ = _c(2);
  const {
    href
  } = t0;
  let t1;
  if ($[0] !== href) {
    t1 = <Menu.Item component="a" href={href} className={styles.accountMenuItem}>Log out</Menu.Item>;
    $[0] = href;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function AccountChipMenu(t0) {
  const $ = _c(19);
  const {
    user,
    links,
    logoutHref
  } = t0;
  const t1 = `Account menu for ${user.displayName}`;
  let t2;
  if ($[0] !== user) {
    t2 = <AccountChipFace user={user} />;
    $[0] = user;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ChevronDown size={16} strokeWidth={1.5} aria-hidden={true} />;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== t1 || $[4] !== t2) {
    t4 = <Menu.Target><button type="button" className={`${styles.parentChip} ${styles.parentChipButton}`} aria-haspopup="menu" aria-label={t1}>{t2}{t3}</button></Menu.Target>;
    $[3] = t1;
    $[4] = t2;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== links) {
    t5 = links.map(_temp);
    $[6] = links;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== links.length) {
    t6 = links.length > 0 && <Menu.Divider />;
    $[8] = links.length;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== logoutHref) {
    t7 = <LogoutItem href={logoutHref} />;
    $[10] = logoutHref;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t5 || $[13] !== t6 || $[14] !== t7) {
    t8 = <Menu.Dropdown className={styles.accountMenu}>{t5}{t6}{t7}</Menu.Dropdown>;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t4 || $[17] !== t8) {
    t9 = <><div className={styles.accountDesktop}><Menu trigger="click-hover" openDelay={0} closeDelay={100}>{t4}{t8}</Menu></div></>;
    $[16] = t4;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  return t9;
}
function _temp(link) {
  return <Menu.Item key={link._key} component="a" href={link.href} className={styles.accountMenuItem} leftSection={menuItemIcon(link)} {...linkTargetProps(link.openInNewTab)}>{link.label}</Menu.Item>;
}
function AccountMenu(t0) {
  const $ = _c(16);
  const {
    user,
    parentPortalHref,
    logoutHref
  } = t0;
  const t1 = `Account menu for ${user.displayName}`;
  let t2;
  if ($[0] !== user.displayName) {
    t2 = <Typography as="span" variant="buttonMd" className={styles.accountName}>{user.displayName}</Typography>;
    $[0] = user.displayName;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ChevronDown size={16} strokeWidth={1.5} aria-hidden={true} />;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== t1 || $[4] !== t2) {
    t4 = <Menu.Target><button type="button" className={styles.accountButton} aria-haspopup="menu" aria-label={t1}>{t2}{t3}</button></Menu.Target>;
    $[3] = t1;
    $[4] = t2;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== parentPortalHref) {
    t5 = <Menu.Item component="a" href={parentPortalHref} className={styles.accountMenuItem}>Parent Portal</Menu.Item>;
    $[6] = parentPortalHref;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== logoutHref) {
    t6 = <LogoutItem href={logoutHref} />;
    $[8] = logoutHref;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t5 || $[11] !== t6) {
    t7 = <Menu.Dropdown className={styles.accountMenu}>{t5}{t6}</Menu.Dropdown>;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t4 || $[14] !== t7) {
    t8 = <Menu>{t4}{t7}</Menu>;
    $[13] = t4;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
}
function DiscoveryHeaderAuthLinks(t0) {
  const $ = _c(29);
  const {
    user,
    accountLinks: t1,
    parentPortalHref,
    loginPath,
    logoutPath,
    signUpHref,
    redirectOrigin,
    redirectPath,
    placement: t2
  } = t0;
  const accountLinks = t1 === undefined ? EMPTY_LINKS : t1;
  const placement = t2 === undefined ? "bar" : t2;
  if (!user) {
    const t3 = placement === "menu" ? styles.mobileGuestLinks : styles.desktopGuestLinks;
    let t4;
    if ($[0] !== loginPath || $[1] !== redirectPath) {
      t4 = authRedirectHref(loginPath, redirectPath);
      $[0] = loginPath;
      $[1] = redirectPath;
      $[2] = t4;
    } else {
      t4 = $[2];
    }
    let t5;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <Typography as="span" variant="buttonMd">Sign in</Typography>;
      $[3] = t5;
    } else {
      t5 = $[3];
    }
    let t6;
    if ($[4] !== t4) {
      t6 = <a href={t4} className={styles.signIn}>{t5}</a>;
      $[4] = t4;
      $[5] = t6;
    } else {
      t6 = $[5];
    }
    let t7;
    if ($[6] !== redirectOrigin || $[7] !== redirectPath || $[8] !== signUpHref) {
      t7 = authRedirectHref(signUpHref, absoluteRedirectUrl(redirectOrigin, redirectPath));
      $[6] = redirectOrigin;
      $[7] = redirectPath;
      $[8] = signUpHref;
      $[9] = t7;
    } else {
      t7 = $[9];
    }
    let t8;
    let t9;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <Typography as="span" variant="buttonMd">Sign up</Typography>;
      t9 = <ArrowUpRight size={16} strokeWidth={1.5} aria-hidden={true} />;
      $[10] = t8;
      $[11] = t9;
    } else {
      t8 = $[10];
      t9 = $[11];
    }
    let t10;
    if ($[12] !== t7) {
      t10 = <a href={t7} className={styles.signUp}>{t8}{t9}</a>;
      $[12] = t7;
      $[13] = t10;
    } else {
      t10 = $[13];
    }
    let t11;
    if ($[14] !== t10 || $[15] !== t3 || $[16] !== t6) {
      t11 = <div className={t3}>{t6}{t10}</div>;
      $[14] = t10;
      $[15] = t3;
      $[16] = t6;
      $[17] = t11;
    } else {
      t11 = $[17];
    }
    return t11;
  }
  if (placement === "menu") {
    return null;
  }
  let t3;
  if ($[18] !== logoutPath || $[19] !== redirectPath) {
    t3 = authRedirectHref(logoutPath, redirectPath);
    $[18] = logoutPath;
    $[19] = redirectPath;
    $[20] = t3;
  } else {
    t3 = $[20];
  }
  const logoutHref = t3;
  if (user.isParent || user.isStudent) {
    let t4;
    if ($[21] !== accountLinks || $[22] !== logoutHref || $[23] !== user) {
      t4 = <AccountChipMenu user={user} links={accountLinks} logoutHref={logoutHref} />;
      $[21] = accountLinks;
      $[22] = logoutHref;
      $[23] = user;
      $[24] = t4;
    } else {
      t4 = $[24];
    }
    return t4;
  }
  let t4;
  if ($[25] !== logoutHref || $[26] !== parentPortalHref || $[27] !== user) {
    t4 = <AccountMenu user={user} parentPortalHref={parentPortalHref} logoutHref={logoutHref} />;
    $[25] = logoutHref;
    $[26] = parentPortalHref;
    $[27] = user;
    $[28] = t4;
  } else {
    t4 = $[28];
  }
  return t4;
}
function DiscoveryHeaderAuthWithQuery(t0) {
  const $ = _c(8);
  let pathname;
  let props;
  if ($[0] !== t0) {
    ({
      pathname,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = pathname;
    $[2] = props;
  } else {
    pathname = $[1];
    props = $[2];
  }
  const t1 = useSearchParams();
  let t2;
  if ($[3] !== t1) {
    t2 = t1.toString();
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const query = t2;
  const redirectPath = query ? `${pathname}?${query}` : pathname;
  let t3;
  if ($[5] !== props || $[6] !== redirectPath) {
    t3 = <DiscoveryHeaderAuthLinks {...props} redirectPath={redirectPath} />;
    $[5] = props;
    $[6] = redirectPath;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
export function DiscoveryHeaderAuth(props) {
  const $ = _c(3);
  const pathname = usePathname() || "/";
  let t0;
  if ($[0] !== pathname || $[1] !== props) {
    t0 = <Suspense fallback={<DiscoveryHeaderAuthLinks {...props} redirectPath={pathname} />}><DiscoveryHeaderAuthWithQuery {...props} pathname={pathname} /></Suspense>;
    $[0] = pathname;
    $[1] = props;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}
export function DiscoveryHeaderAccountMobileLinks(t0) {
  const $ = _c(14);
  const {
    user,
    links,
    logoutPath
  } = t0;
  const pathname = usePathname() || "/";
  let t1;
  if ($[0] !== logoutPath || $[1] !== pathname) {
    t1 = authRedirectHref(logoutPath, pathname);
    $[0] = logoutPath;
    $[1] = pathname;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== links || $[4] !== t1 || $[5] !== user) {
    t2 = <AccountMobileLinkList user={user} links={links} logoutHref={t1} />;
    $[3] = links;
    $[4] = t1;
    $[5] = user;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== links || $[8] !== logoutPath || $[9] !== user) {
    t3 = <AccountMobileLinksWithQuery user={user} links={links} logoutPath={logoutPath} />;
    $[7] = links;
    $[8] = logoutPath;
    $[9] = user;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== t2 || $[12] !== t3) {
    t4 = <Suspense fallback={t2}>{t3}</Suspense>;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
}
function AccountMobileLinksWithQuery(t0) {
  const $ = _c(9);
  const {
    user,
    links,
    logoutPath
  } = t0;
  const pathname = usePathname() || "/";
  const t1 = useSearchParams();
  let t2;
  if ($[0] !== t1) {
    t2 = t1.toString();
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const query = t2;
  const redirectPath = query ? `${pathname}?${query}` : pathname;
  let t3;
  if ($[2] !== logoutPath || $[3] !== redirectPath) {
    t3 = authRedirectHref(logoutPath, redirectPath);
    $[2] = logoutPath;
    $[3] = redirectPath;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== links || $[6] !== t3 || $[7] !== user) {
    t4 = <AccountMobileLinkList user={user} links={links} logoutHref={t3} />;
    $[5] = links;
    $[6] = t3;
    $[7] = user;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}
function AccountMobileLinkList(t0) {
  const $ = _c(15);
  const {
    user,
    links,
    logoutHref
  } = t0;
  const close = useDiscoveryMobileMenuClose();
  let t1;
  if ($[0] !== user.displayName) {
    t1 = <li className={styles.mobileItem}><span className={styles.mobileDirectLink}><Typography as="span" variant="h5">{user.displayName}</Typography></span></li>;
    $[0] = user.displayName;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== close || $[3] !== links) {
    let t3;
    if ($[5] !== close) {
      t3 = link => <li key={link._key} className={styles.mobileItem}><a href={link.href} className={link.iconUrl || link.lucideIcon ? `${styles.mobileDirectLink} ${styles.mobileAccountLink} ${styles.mobileMenuLink}` : `${styles.mobileDirectLink} ${styles.mobileAccountLink}`} {...linkTargetProps(link.openInNewTab)} onClick={close}><DiscoveryNavLinkIcon link={link} variant="inline" /><Typography as="span" variant="h5">{link.label}</Typography></a></li>;
      $[5] = close;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    t2 = links.map(t3);
    $[2] = close;
    $[3] = links;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Typography as="span" variant="h5">Log out</Typography>;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== close || $[9] !== logoutHref) {
    t4 = <li className={styles.mobileItem}><a href={logoutHref} className={`${styles.mobileDirectLink} ${styles.mobileAccountLink}`} onClick={close}>{t3}</a></li>;
    $[8] = close;
    $[9] = logoutHref;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t1 || $[12] !== t2 || $[13] !== t4) {
    t5 = <>{t1}{t2}{t4}</>;
    $[11] = t1;
    $[12] = t2;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  return t5;
}